/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { IDBPDatabase, IndexKey, IndexNames, StoreKey, StoreNames, StoreValue } from 'idb';
import { KaryaDB } from '../Setup';
import { err, ok } from '@karya/error-values';
import {
  DeleteRecordResult,
  GetAllResult,
  GetSingleByIndexResult,
  GetSingleResult,
  InsertRecordResult,
  InsertManyResult,
  GetCountResult,
} from './Results';
import { IndexedDBError, RecordNotFoundError } from './Error';

/**
 * DatabaseModel provides an abstraction for interacting with an IndexedDB database.
 *
 * It includes methods for CRUD operations and utility methods for fetching records
 * by index values, handling database errors gracefully using the `tryAsync` utility.
 */
export class DatabaseModel {
  /**
   * Constructs a new instance of `DatabaseModel`, which provides an abstraction layer for IndexedDB.
   * This class simplifies CRUD operations and error handling with indexeddb-promised methods.
   *
   * @param db - An instance of `IDBPDatabase`, used to execute IndexedDB operations.
   */
  constructor(protected db: IDBPDatabase<KaryaDB>) {}

  /**
   * Retrieves a single record from the specified store by its key.
   *
   * If the record is not found, an error result is returned.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to retrieve the record from.
   * @param key - The key of the record to retrieve.
   * @returns A result containing the retrieved record or an error.
   */
  async getSingle<Store extends StoreNames<KaryaDB>>(
    storeName: Store,
    key: StoreKey<KaryaDB, Store>,
  ): GetSingleResult<Store> {
    const [record, error] = await this.db
      .get(storeName, key)
      .intoResult((error) => new IndexedDBError({ operation: 'get', cause: error, storeName, key }))
      .unwrapResult()
      .toTuple();

    if (error) {
      return err(error);
    }

    if (record === undefined) {
      return err(new RecordNotFoundError<Store, never>({ storeName, key }));
    }
    return ok(record);
  }

  /**
   * Inserts a new record into the specified store.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to add the record to.
   * @param value - The value of the record to insert.
   * @returns A result indicating the success or failure of the operation.
   */
  async insertRecord<Store extends StoreNames<KaryaDB>>(
    storeName: Store,
    value: StoreValue<KaryaDB, Store>,
  ): InsertRecordResult<Store> {
    return await this.db
      .add(storeName, value)
      .intoResult((error) => new IndexedDBError({ operation: 'add', cause: error, storeName, value }));
  }

  /**
   * Inserts multiple records into the specified store.
   * Fails the entire operation if any insert fails.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to add the records to.
   * @param values - An array of values to insert into the store.
   * @returns A result indicating the success or failure of the operation.
   */
  async insertMany<Store extends StoreNames<KaryaDB>>(
    storeName: Store,
    values: StoreValue<KaryaDB, Store>[],
  ): InsertManyResult<Store> {
    const transaction = this.db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const [results, error] = await Promise.all(values.map((value) => store.add(value)))
      .intoResult((error) => new IndexedDBError({ operation: 'add', cause: error, storeName, value: values }))
      .unwrapResult()
      .toTuple();

    if (error) {
      transaction.abort();
      return err(error);
    }

    return ok(results);
  }

  /**
   * Updates or inserts a record in the specified store.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to upsert the record in.
   * @param value - The value of the record to upsert.
   * @returns A result indicating the success or failure of the operation.
   */
  async upsertRecord<Store extends StoreNames<KaryaDB>>(
    storeName: Store,
    value: StoreValue<KaryaDB, Store>,
  ): InsertRecordResult<Store> {
    return await this.db
      .put(storeName, value)
      .intoResult((error) => new IndexedDBError({ operation: 'put', cause: error, storeName, value }));
  }

  /**
   * Updates or inserts multiple records in the specified store.
   * Fails the entire operation if any upsert fails.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to upsert the records in.
   * @param values - An array of values to upsert into the store.
   * @returns A result indicating the success or failure of the operation.
   */
  async upsertRecords<Store extends StoreNames<KaryaDB>>(
    storeName: Store,
    values: StoreValue<KaryaDB, Store>[],
  ): InsertManyResult<Store> {
    const transaction = this.db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const [results, error] = await Promise.all(values.map((value) => store.put(value)))
      .intoResult((error) => new IndexedDBError({ operation: 'put', cause: error, storeName, value: values }))
      .unwrapResult()
      .toTuple();

    if (error) {
      transaction.abort();
      return err(error);
    }

    return ok(results);
  }

  /**
   * Deletes a record from the specified store by its key.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to delete the record from.
   * @param key - The key of the record to delete.
   * @returns A result indicating the success or failure of the operation.
   */
  async deleteRecord<Store extends StoreNames<KaryaDB>>(
    storeName: Store,
    key: StoreKey<KaryaDB, Store>,
  ): DeleteRecordResult {
    return await this.db
      .delete(storeName, key)
      .intoResult((error) => new IndexedDBError({ operation: 'delete', cause: error, storeName, key }));
  }

  /**
   * Clears all records from the specified store.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to clear.
   * @returns A result indicating the success or failure of the operation.
   */
  async clearStore<Store extends StoreNames<KaryaDB>>(storeName: Store): DeleteRecordResult {
    return await this.db
      .clear(storeName)
      .intoResult((error) => new IndexedDBError({ operation: 'clear', cause: error, storeName }));
  }

  /**
   * Retrieves a single record from the specified store by an index value.
   *
   * If the record is not found, an error result is returned.
   *
   * @template Store - The type of the store in IndexedDB.
   * @template IndexName - The name of the index in the store.
   * @param storeName - The name of the store to retrieve the record from.
   * @param indexName - The name of the index to use for retrieval.
   * @param indexKey - The key value of the index to search for.
   * @returns A result containing the retrieved record or an error.
   */
  async getSingleByIndex<Store extends StoreNames<KaryaDB>, IndexName extends IndexNames<KaryaDB, Store>>(
    storeName: Store,
    indexName: IndexName,
    indexKey: IndexKey<KaryaDB, Store, IndexName>,
  ): GetSingleByIndexResult<Store, IndexName> {
    const [record, error] = await this.db
      .getFromIndex(storeName, indexName, indexKey)
      .intoResult(
        (error) => new IndexedDBError({ operation: 'get', cause: error, storeName, indexName, key: indexKey }),
      )
      .unwrapResult()
      .toTuple();

    if (error) {
      return err(error);
    }

    if (record === undefined) {
      return err(new RecordNotFoundError<Store, IndexName>({ storeName, key: indexKey, indexName }));
    }
    return ok(record);
  }

  /**
   * Retrieves all records from the specified store.
   *
   * @template Store - The type of the store in IndexedDB.
   * @param storeName - The name of the store to retrieve records from.
   * @returns A result containing an array of all records or an error.
   */
  async getAll<Store extends StoreNames<KaryaDB>>(storeName: Store): GetAllResult<Store> {
    return await this.db
      .getAll(storeName)
      .intoResult((error) => new IndexedDBError({ operation: 'get', cause: error, storeName }));
  }

  /**
   * Retrieves all records from the specified store that match the given index key.
   *
   * @template Store - The type of the store in IndexedDB.
   * @template IndexName - The name of the index in the store.
   * @param storeName - The name of the store to retrieve records from.
   * @param indexName - The name of the index to use for retrieval.
   * @param indexKey - The key value of the index to search for.
   * @returns A result containing an array of matching records or an error.
   */
  async getAllFromIndex<Store extends StoreNames<KaryaDB>, IndexName extends IndexNames<KaryaDB, Store>>(
    storeName: Store,
    indexName: IndexName,
    indexKey?: IndexKey<KaryaDB, Store, IndexName>,
  ): GetAllResult<Store> {
    return await this.db
      .getAllFromIndex(storeName, indexName, indexKey)
      .intoResult(
        (error) => new IndexedDBError({ operation: 'get', cause: error, storeName, indexName, key: indexKey }),
      );
  }

  /**
   * Get the total number of records in the specified store.
   * @param storeName - The name of the store to count records from.
   * @returns A result containing the count or an IndexedDBError on failure.
   */
  async getCount<Store extends StoreNames<KaryaDB>>(storeName: Store): GetCountResult {
    return await this.db
      .count(storeName)
      .intoResult((err) => new IndexedDBError({ operation: 'count', storeName, cause: err }));
  }

  /**
   * Get the number of records in a store matching a specific index and optional key.
   * @param storeName - The name of the store to count records from.
   * @param indexName - The name of the index to use for counting.
   * @param indexKey - Optional key to filter the index count.
   * @returns A result containing the count or an IndexedDBError on failure.
   */
  async getCountFromIndex<Store extends StoreNames<KaryaDB>, IndexName extends IndexNames<KaryaDB, Store>>(
    storeName: Store,
    indexName: IndexName,
    indexKey?: IndexKey<KaryaDB, Store, IndexName>,
  ): GetCountResult {
    return await this.db
      .countFromIndex(storeName, indexName, indexKey)
      .intoResult((err) => new IndexedDBError({ operation: 'count', storeName, indexName, key: indexKey, cause: err }));
  }
}
